import type { PublicationCode } from '@/scopes/common/domain/models'
import type { SearchFilters } from '@/scopes/search/search/infrastructure/api/types'
import { useDecision } from '@optimizely/react-sdk'
import { mqMin, space, tok, tokPx, useUdsToken, zIndex } from '@sunwebgroup/ui-components'
import { IndeterminateProgressCircle, Layout } from '@sunwebgroup/ui-components/uds'
import { QueryClientProvider } from '@tanstack/react-query'
import { use, useEffect, useMemo, useRef } from 'react'
import { css, styled } from 'styled-components'
import { useCMSPageField } from '@/cms/fields'
import { type DateOrRange, departureDateStore } from '@/infrastructure/departure-date/store'
import { Custom500 } from '@/pages/500'
import { Footer } from '@/scopes/common/components'
import { CatalogueDetailsContext } from '@/scopes/common/components/CatalogueDetailsContext'
import { DevTools } from '@/scopes/common/components/DevTools/DevTools'
import { roomsParticipantsDistributionStore } from '@/scopes/common/components/Filters/Participants/store'
import { CatalogueContent } from '@/scopes/common/components/Footer/CatalogueContent'

import { Header } from '@/scopes/common/components/Header'
import { useIntersection } from '@/scopes/common/components/hooks/useIntersection'
import {
  AbTests,
  OPTIMIZELY_TESTS_KEYS,
} from '@/scopes/common/infrastructure/ab-testing/optimizely/types'
import { useDecisionWhenReady } from '@/scopes/common/infrastructure/ab-testing/optimizely/useDecisionWhenReady'
import {
  OptimizelyFlagContext,
  useOptimizelyFlagContext,
} from '@/scopes/common/infrastructure/ab-testing/optimizely/useOptimizely'
import { useSendOptimizelyEvent } from '@/scopes/common/infrastructure/ab-testing/optimizely/useSendOptimizelyEvent'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { useCommonDependency } from '@/scopes/common/infrastructure/dependencies/common'
import { NewRelicErrorBoundary } from '@/scopes/common/infrastructure/logging/NewRelicErrorBoundary'
import {
  createBrowserQueryStringKeepingParams,
} from '@/scopes/common/infrastructure/query-strings/browserQueryStringKeepingParams'
import { useAddFavoriteFromUrl } from '@/scopes/favourites/domain/hooks'
import { getDefaultSearchSelection } from '@/scopes/search/domain/policies/defaultFilters'
import { createSelectDateFilterActions } from '@/scopes/search/search/domain/actions/selectDateFilterActions'
import { createSelectDestinationActions } from '@/scopes/search/search/domain/actions/selectDestinationActions'
import { SEARCH_ELEMENT } from '@/scopes/search/search/domain/constants'
import { createFilterSelectionQueryParamHandler } from '@/scopes/search/search/domain/filterSelectionQueryParamHandler'
import { useAIFilterQuery, useResults, useSearchQuerySync } from '@/scopes/search/search/domain/hooks'
import {
  createQueryStringToSearchStateMapperIgnoreCasing,
} from '@/scopes/search/search/domain/queryStringToSearchStateMapperIgnoreCasing'
import { getSortingAccordingToTestAndPartyComposition } from '@/scopes/search/search/domain/sorting'
import { createSearchApiClient } from '@/scopes/search/search/infrastructure/api/searchApiClient'
import {
  createCampaignDetailsApiClient,
} from '@/scopes/search/search/infrastructure/campaign-details/campaignDetailsApiClient'
import { searchQueryClient } from '@/scopes/search/search/infrastructure/query-client/searchQueryClient'
import { useSearchQueryRepository } from '@/scopes/search/search/infrastructure/repositories/useSearchQueryRepository'
import { searchStore } from '@/scopes/search/search/infrastructure/store/store'
import {
  getFlexibleRangeForDepartureDate,
} from '@/scopes/search/search/pages/SearchPage/components/QuickSearchFilters/DepartureDateFilter/utils'
import { SideBar } from '@/scopes/search/search/pages/SearchPage/components/SideBar'
import { searchPassThroughParams, type SearchPassThroughParams } from '@/scopes/search/types'
import { QuickSearchBar } from '../../../components/QuickSearchBar'
import { createSearchResponseMapper } from '../../infrastructure/api/searchResponseMapper'
import { FiltersModal } from './components/FiltersModal'
import { Hero } from './components/Hero'
import { Results } from './components/Results'
import { SearchDependenciesProvider } from './context'

const LandingHeader = styled.div`
  position: relative;
  z-index: ${zIndex.level3};
  padding-top: ${space('primitives_space_16')};
  width: 100%;
  height: ${space('primitives_space_96')};

  @media ${mqMin('primitives_breakpoint_m_min')} {
    padding-top: ${space('primitives_space_24')};
  }
`

const StyledLoadingSpinner = styled(IndeterminateProgressCircle)<{ $isLandingPage?: boolean }>`
  margin-top: ${({ $isLandingPage }) => $isLandingPage ? 'unset' : '80px'};
  grid-column: span 2;
`

const Wrapper = styled('main')<{ $isLandingPage?: boolean }>`
  background-color: ${tok('semantics_views_tinted_background_color')};
  min-height: ${({ $isLandingPage }) => $isLandingPage ? '1vh' : '100vh'};

  ${({ $isLandingPage }) => $isLandingPage
    && css`
      margin-bottom: ${space('primitives_space_16')};
      @media ${mqMin('primitives_breakpoint_m_min')} {
        margin-bottom: ${space('primitives_space_32')};
      }

      @media ${mqMin('primitives_breakpoint_l_min')} {
        margin-bottom: ${space('primitives_space_48')};
      }
    `
  }
}
`

const Content = styled(Layout)<{ $splitDetailsEnabled: boolean }>`
  display: grid;
  grid-template-columns: 100%;
  gap: ${space('primitives_space_24')};
  padding-top: ${tokPx('semantics_layouts_s_margin_top_and_bottom')};
  padding-bottom: ${tokPx('semantics_layouts_s_margin_top_and_bottom')};

  @media ${mqMin('primitives_breakpoint_m_min')} {
    padding-top: ${tokPx('semantics_layouts_m_margin_top_and_bottom')};
    padding-bottom: ${tokPx('semantics_layouts_m_margin_top_and_bottom')};
  }
  @media ${mqMin('primitives_breakpoint_l_min')} {
    padding-top: ${tokPx('semantics_layouts_l_margin_top_and_bottom')};
    padding-bottom: ${tokPx('semantics_layouts_l_margin_top_and_bottom')};
  }

  ${({ $splitDetailsEnabled }) =>
    $splitDetailsEnabled
      ? css`
        @media (min-width: 1280px) {
          grid-template-columns: minmax(303px, 383px) minmax(600px, auto);
          gap: ${space('primitives_space_32')};
        }
      `
      : css`
        @media ${mqMin('primitives_breakpoint_l_min')} {
          grid-template-columns: minmax(303px, 383px) minmax(600px, auto);
          gap: ${space('primitives_space_32')};
        }
      `}
  ${StyledLoadingSpinner} {
    align-self: center;
    justify-self: center;
  }
`

const Sentinel = styled.div`
  background: transparent;
  height: 1px;
  margin-bottom: -1px;
  width: 100%;
`

function sanitizeQueryString(input: string): string {
  if (!input)
    return ''
  const trimmed = input.trim()
  const query = trimmed.includes('://')
    ? trimmed.split('?')[1] || ''
    : trimmed.replace(/^&/, '').replace(/^\??/, '')
  return query ? `?${query}` : ''
}

function cleanQueryParams(combinedQueryString: string): string {
  const params = new URLSearchParams(combinedQueryString)
  const paramMap = new Map<string, string>()

  // Store only the last value for each key
  params.forEach((value, key) => {
    paramMap.set(key, value)
  })

  const cleanedParams = new URLSearchParams()
  paramMap.forEach((value, key) => {
    cleanedParams.set(key, value)
  })

  return `?${cleanedParams.toString()}`
}

function Search({ contextItemId, isLandingPage, isActive, publicationCode }: {
  contextItemId: string
  isLandingPage?: boolean
  isActive: boolean
  publicationCode: string
}) {
  useAIFilterQuery()
  useSearchQuerySync()
  const { isLoading, isFetching } = useResults()
  const progressStateSize = Number(useUdsToken('primitives_space_32'))
  const defaultQuery = useCMSPageField('SearchPage.defaultQuery')
  const { productCardSplitDetails, prioritySorting } = useOptimizelyFlagContext()
  const brandName = useConfiguration('brandName')
  useAddFavoriteFromUrl(contextItemId)

  useEffect(() => {
    if (!isActive)
      return

    const defaultValues = getDefaultSearchSelection(publicationCode as PublicationCode)
    const queryStringToSearchStateMapper = createQueryStringToSearchStateMapperIgnoreCasing({ defaultValues })
    const combinedQueryString = defaultQuery
      ? `${window.location.search}${sanitizeQueryString(defaultQuery).replace('?', '&')}`
      : window.location.search

    const cleanedCombinedQueryString = cleanQueryParams(combinedQueryString)

    const initialState = queryStringToSearchStateMapper.map(cleanedCombinedQueryString)
    const sortingBy = getSortingAccordingToTestAndPartyComposition(initialState, prioritySorting)

    searchStore.setState({ ...initialState, sorting: { by: sortingBy } })

    if (initialState.filterSelection?.DepartureDate) {
      const departureDate = initialState.filterSelection?.DepartureDate as DateOrRange

      departureDateStore.setState({
        departureDate,
        flexibleRange: getFlexibleRangeForDepartureDate(departureDate),
        originDate: '',
        view: 'calendar',
      })
    }
    if (initialState.filterSelection?.Participants) {
      roomsParticipantsDistributionStore.setState({
        roomsParticipantDistribution: initialState.filterSelection?.Participants,
      })
    }
  }, [isActive, publicationCode, defaultQuery, prioritySorting])

  const sentinelRef = useRef<HTMLDivElement>(null)
  const sentinelBottomRef = useRef<HTMLDivElement>(null)
  const observer = useIntersection(sentinelRef, {
    root: null,
    rootMargin: '25px',
    threshold: 1,
  })
  const observerBottom = useIntersection(sentinelBottomRef, {
    root: null,
    rootMargin: '25px',
    threshold: 1,
  })
  const currentPosition = sentinelRef?.current?.getBoundingClientRect().y ?? 0
  const showButtonWhenListIsReached = observer ? observer.isIntersecting || currentPosition < 0 : false
  const currentPositionBottom = sentinelBottomRef?.current?.getBoundingClientRect().y ?? 0
  const hideButtonWhenFooterIsReached = observerBottom ? !observerBottom.isIntersecting && currentPositionBottom < 0 : true
  const isListVisible = !hideButtonWhenFooterIsReached && showButtonWhenListIsReached
  const showSticky = observer ? !observer.isIntersecting && currentPosition < 0 && !hideButtonWhenFooterIsReached : false
  const { sendEvent: sendOptimizelyEvent } = useSendOptimizelyEvent()

  useEffect(() => {
    sendOptimizelyEvent('plp_reached')
  }, [])

  return (
    <NewRelicErrorBoundary scope="search-page" fallback={<Custom500 brand={brandName} />}>
      <Wrapper $isLandingPage={isLandingPage} id="main-content" tabIndex={-1}>
        <Sentinel ref={sentinelRef} />
        {!isLandingPage && (
          <Hero>{!isLoading && <QuickSearchBar disabled={isFetching} showSticky={showSticky} />}</Hero>)}
        {isLandingPage && !isLoading && (
          <LandingHeader>
            <QuickSearchBar
              disabled={isFetching}
              hasPadding={false}
              showSticky={showSticky}
            />
          </LandingHeader>
        )}
        <Content $splitDetailsEnabled={productCardSplitDetails}>
          {isLoading
            ? (
                <StyledLoadingSpinner $isLandingPage={isLandingPage} size={progressStateSize} />
              )
            : (
                <>
                  <SideBar />
                  <Results />
                  {isListVisible && <FiltersModal isLandingPage={isLandingPage} />}
                </>
              )}
        </Content>
      </Wrapper>
      <Sentinel ref={sentinelBottomRef} />
    </NewRelicErrorBoundary>
  )
}

const SearchFooter = styled(Footer)`
  background-color: ${tok('semantics_views_tinted_background_color')};
`

interface SearchPageProps {
  isLandingPage?: boolean
}

function SearchPageContent({ isLandingPage }: SearchPageProps) {
  const { setPublicationCode } = use(CatalogueDetailsContext)
  const [productCardSplitDetailsDecision] = useDecision(OPTIMIZELY_TESTS_KEYS.FOLLOW_UP_SPLIT_PACKAGES)
  const { isReady: isSortingExperimentReady, enabled: enableSortingPersonalization } = useDecisionWhenReady({ key: OPTIMIZELY_TESTS_KEYS.SORT_ORDER_PERSONALIZATION })
  const configPublicationCode = useConfiguration('publicationCode')
  const modelPublicationCode = useCMSPageField('SearchPage.publicationCode')
  const publicationCode = configPublicationCode !== 'NONE' ? configPublicationCode : modelPublicationCode
  const contextItemId = useCMSPageField('contextItemId')
  const promotionId = useCMSPageField('SearchPage.promotionId')
  const mainSearchPageId = useCMSPageField('SearchPage.searchPageId')
  const queryStringClient = useCommonDependency('queryStringClient')
  const searchQueryRepository = useSearchQueryRepository()

  const clientDependencies = useMemo(() => {
    const browserQuery = createBrowserQueryStringKeepingParams<SearchPassThroughParams>({
      passThroughParams: searchPassThroughParams,
      queryStringClient,
    })
    const abTests: AbTests = {
      FOLLOW_UP_SPLIT_PACKAGES: productCardSplitDetailsDecision.enabled && OPTIMIZELY_TESTS_KEYS.FOLLOW_UP_SPLIT_PACKAGES.toLowerCase().includes(publicationCode.toString().toLowerCase()),
      SORT_ORDER_PERSONALIZATION: enableSortingPersonalization && OPTIMIZELY_TESTS_KEYS.SORT_ORDER_PERSONALIZATION.toLowerCase().includes(publicationCode.toString().toLowerCase()),
    }
    return {
      contextItemId: isLandingPage ? mainSearchPageId : contextItemId,
      promotionId: isLandingPage ? promotionId : undefined,
      browserQuery,
      searchResponseMapper: createSearchResponseMapper(),
      abTests,
    }
  }, [queryStringClient, productCardSplitDetailsDecision.enabled, publicationCode, isLandingPage, mainSearchPageId, contextItemId, promotionId, enableSortingPersonalization])

  const filterSelectionQueryParamHandler = useMemo(() => createFilterSelectionQueryParamHandler({
    store: searchStore,
    browserQuery: clientDependencies.browserQuery,
  }), [clientDependencies.browserQuery])

  const dateFilterActions = useMemo(
    () => createSelectDateFilterActions({
      filterSelectionQueryParamHandler,
      searchQueryRepository,
    }),
    [filterSelectionQueryParamHandler, searchQueryRepository],
  )

  const destinationFilterActions = useMemo(
    () => createSelectDestinationActions({
      filterSelectionQueryParamHandler,
      searchQueryRepository,
    }),
    [filterSelectionQueryParamHandler, searchQueryRepository],
  )

  const optimizelyFlagContextValue = useMemo(
    () => ({
      productCardSplitDetails: productCardSplitDetailsDecision.enabled && OPTIMIZELY_TESTS_KEYS.FOLLOW_UP_SPLIT_PACKAGES.toLowerCase().includes(publicationCode.toString().toLowerCase()),
      prioritySorting: enableSortingPersonalization && OPTIMIZELY_TESTS_KEYS.SORT_ORDER_PERSONALIZATION.toLowerCase().includes(publicationCode.toString().toLowerCase()),
    }),
    [productCardSplitDetailsDecision.enabled, enableSortingPersonalization, publicationCode],
  )

  useEffect(() => {
    setPublicationCode(publicationCode)
  }, [publicationCode])

  return (
    <SearchDependenciesProvider
      dependencies={{
        filterSelectionQueryParamHandler,
        searchAPIClient: createSearchApiClient<SearchFilters>(clientDependencies),
        campaignDetailsApiClient: createCampaignDetailsApiClient({ contextItemId }),
        dateFilterActions,
        destinationFilterActions,
      }}
    >
      <OptimizelyFlagContext
        value={optimizelyFlagContextValue}
      >
        <div id={SEARCH_ELEMENT}>
          {!isLandingPage && <Header />}
          <Search
            contextItemId={contextItemId}
            isActive={isSortingExperimentReady}
            isLandingPage={isLandingPage}
            publicationCode={publicationCode}
          />
          {!isLandingPage && (
            <SearchFooter>
              <CatalogueContent />
            </SearchFooter>
          )}
        </div>
        <DevTools />
      </OptimizelyFlagContext>
    </SearchDependenciesProvider>
  )
}

export function HybridSearchPage(props: SearchPageProps) {
  return <SearchPageContent {...props} />
}

function SearchPage(props: SearchPageProps) {
  return (
    <QueryClientProvider client={searchQueryClient}>
      <SearchPageContent {...props} />
    </QueryClientProvider>
  )
}

export default SearchPage
